import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Monitor, RefreshCw } from 'lucide-react';

import { api } from '../../services/api';
import type { Bot } from '../../types';
import { useTranslation } from '../../i18n/I18nContext';

export function FreqtradeBots() {
  const { t } = useTranslation();
  const [selectedBotId, setSelectedBotId] = useState<string>('');
  const [frameLoading, setFrameLoading] = useState(true);
  const [frameRefreshNonce, setFrameRefreshNonce] = useState(0);

  const {
    data: bots = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['freqtrade-bots'],
    queryFn: async (): Promise<Bot[]> => {
      const response = await api.get<Bot[]>('/bots');
      return (response.data || []).filter((bot) => !!bot.api_url);
    },
    refetchInterval: 30000,
  });

  useEffect(() => {
    if (!bots.length) {
      setSelectedBotId('');
      return;
    }

    const selectedStillExists = bots.some((bot) => bot.id === selectedBotId);
    if (!selectedBotId || !selectedStillExists) {
      setSelectedBotId(bots[0].id);
      setFrameLoading(true);
    }
  }, [bots, selectedBotId]);

  const selectedBot = useMemo(
    () => bots.find((bot) => bot.id === selectedBotId) ?? null,
    [bots, selectedBotId]
  );

  const {
    data: embeddedUrl = '',
    isLoading: iframeUrlLoading,
  } = useQuery({
    queryKey: ['freqtrade-bot-ui', selectedBotId, frameRefreshNonce],
    enabled: !!selectedBotId,
    queryFn: async (): Promise<string> => {
      const response = await api.post<{ token: string }>(`/bots/${selectedBotId}/ui-token`);
      const token = response.data?.token;
      if (!token) {
        throw new Error('Failed to create bot UI token');
      }
      const backendOrigin = api.getBaseUrl().replace(/\/api\/v1\/?$/, '');
      const url = new URL(`${backendOrigin}/api/v1/bot-ui/${selectedBotId}/`);
      url.searchParams.set('token', token);
      url.searchParams.set('_dashboard_refresh', String(frameRefreshNonce));
      return url.toString();
    },
  });

  useEffect(() => {
    if (iframeUrlLoading && selectedBotId) {
      setFrameLoading(true);
    }
  }, [iframeUrlLoading, selectedBotId]);

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <RefreshCw className="mx-auto mb-4 h-8 w-8 animate-spin text-blue-500" />
          <p className="text-gray-400">{t('freqtradeBots.loading')}</p>
        </div>
      </div>
    );
  }

  if (!bots.length) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center text-gray-400">
          <Monitor className="mx-auto mb-4 h-16 w-16 opacity-50" />
          <p>{t('freqtradeBots.noneFound')}</p>
          <p className="mt-2 text-sm">{t('freqtradeBots.checkConfig')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-[#30363d] bg-[#161b22] p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <Monitor className="h-5 w-5 text-blue-500" />
            <div>
              <h1 className="text-lg font-semibold text-white">{t('nav.freqtradeBots')}</h1>
              <p className="text-sm text-gray-400">
                {bots.length} {t('freqtradeBots.count')}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <select
              value={selectedBotId}
              onChange={(e) => {
                setSelectedBotId(e.target.value);
                setFrameLoading(true);
              }}
              className="min-w-[240px] rounded-lg border border-[#30363d] bg-[#0f1419] px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {bots.map((bot) => (
                <option key={bot.id} value={bot.id}>
                  {bot.name}
                </option>
              ))}
            </select>

            <button
              onClick={() => {
                void refetch();
                if (selectedBot?.api_url) {
                  setFrameLoading(true);
                  setFrameRefreshNonce((prev) => prev + 1);
                }
              }}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-[#30363d] px-3 py-2 text-sm text-gray-200 hover:bg-[#30363d]"
            >
              <RefreshCw className="h-4 w-4" />
              {t('finance.refresh')}
            </button>

            {selectedBot?.api_url ? (
              <a
                href={selectedBot.api_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                <ExternalLink className="h-4 w-4" />
                {t('freqtradeBots.openDirect')}
              </a>
            ) : null}
          </div>
        </div>
      </div>

      {selectedBot ? (
        <div className="overflow-hidden rounded-xl border border-[#30363d] bg-[#0f1419]">
          <div className="border-b border-[#30363d] bg-[#161b22] px-4 py-3 text-sm text-gray-300">
            <span className="font-medium text-white">{selectedBot.name}</span>
            {selectedBot.strategy ? ` • ${selectedBot.strategy}` : ''}
            {selectedBot.exchange ? ` • ${selectedBot.exchange}` : ''}
          </div>

          <div className="relative h-[70vh] min-h-[520px] bg-[#0f1419]">
            {frameLoading ? (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#0f1419]">
                <div className="text-center">
                  <RefreshCw className="mx-auto mb-4 h-8 w-8 animate-spin text-blue-500" />
                  <p className="text-gray-400">{t('freqtradeBots.loadingBot')} {selectedBot.name}...</p>
                </div>
              </div>
            ) : null}

            {embeddedUrl ? (
              <iframe
                key={embeddedUrl}
                src={embeddedUrl}
                className="h-full w-full border-0"
                onLoad={() => setFrameLoading(false)}
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                title={`Freqtrade ${selectedBot.name}`}
              />
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
